import type { Avatar } from '@kinop/shared';
import { AVATAR_COLORS, AVATAR_FACES } from '@kinop/shared';
/**
 * Локальное хранилище браузера.
 *
 * Обёртки нужны не для красоты: в приватном режиме и при запрете сторонних
 * данных обращение к localStorage бросает исключение. Приложение должно
 * работать и в этом случае — просто без запоминания имени.
 */

const KEYS = {
  clientId: 'kinop.clientId',
  nickname: 'kinop.nickname',
  hostTokens: 'kinop.hostTokens',
  volume: 'kinop.volume',
} as const;

function readRaw(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeRaw(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ничего страшного: настройка просто не переживёт перезагрузку.
  }
}

/**
 * Постоянный идентификатор устройства.
 *
 * По нему сервер понимает, что перезагрузка страницы — это тот же участник,
 * а не новый гость. Никаких персональных данных в нём нет.
 */
export function getClientId(): string {
  const existing = readRaw(KEYS.clientId);
  if (existing && existing.length > 0) return existing;

  const generated =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

  writeRaw(KEYS.clientId, generated);
  return generated;
}

export function getNickname(): string {
  return readRaw(KEYS.nickname) ?? '';
}

export function setNickname(nickname: string): void {
  writeRaw(KEYS.nickname, nickname);
}

export function getVolume(): number {
  const raw = readRaw(KEYS.volume);
  const parsed = raw == null ? Number.NaN : Number(raw);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : 1;
}

export function setVolume(volume: number): void {
  writeRaw(KEYS.volume, String(volume));
}

// ─────────────────────────── токены ведущего ───────────────────────────

/**
 * Секреты созданных комнат: код комнаты → токен.
 * Хранятся у создателя, чтобы права ведущего пережили перезагрузку.
 */
function readHostTokens(): Record<string, string> {
  const raw = readRaw(KEYS.hostTokens);
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, string>) : {};
  } catch {
    return {};
  }
}

export function getHostToken(roomCode: string): string | null {
  return readHostTokens()[roomCode] ?? null;
}

export function saveHostToken(roomCode: string, token: string): void {
  const tokens = readHostTokens();
  tokens[roomCode] = token;

  // Держим только последние 20 комнат, иначе список растёт бесконечно.
  const entries = Object.entries(tokens).slice(-20);
  writeRaw(KEYS.hostTokens, JSON.stringify(Object.fromEntries(entries)));
}


// ─────────────────────────── профиль ───────────────────────────

/**
 * Мордочка и цвет аватарки.
 *
 * Аккаунтов нет, поэтому профиль живёт в браузере и приезжает в комнату
 * вместе с именем. Значения по умолчанию выводим из идентификатора
 * устройства: у человека, ничего не выбиравшего, аватарка всё равно своя,
 * а не общая для всех.
 */
const PROFILE_KEY = 'kinop:profile';

function defaultAvatar(): Avatar {
  const seed = [...getClientId()].reduce((acc, ch) => (acc * 31 + ch.charCodeAt(0)) % 9973, 7);
  return {
    face: '?',
    color: AVATAR_COLORS[seed % AVATAR_COLORS.length]!,
  };
}

export function getAvatar(): Avatar {
  try {
    const raw = window.localStorage.getItem(PROFILE_KEY);
    if (!raw) return defaultAvatar();
    const parsed = JSON.parse(raw) as Partial<Avatar>;
    const face = (AVATAR_FACES as readonly string[]).includes(parsed.face ?? '')
      ? parsed.face!
      : defaultAvatar().face;
    const color = (AVATAR_COLORS as readonly string[]).includes(parsed.color ?? '')
      ? parsed.color!
      : defaultAvatar().color;
    // Фотография — только наш собственный путь: чужой адрес в разметке
    // взяться не должен даже из своего же хранилища.
    const photoUrl =
      typeof parsed.photoUrl === 'string' &&
      /^\/api\/telegram\/avatar\/\d{1,20}$/.test(parsed.photoUrl)
        ? parsed.photoUrl
        : null;
    // Значок — стикер из набора премиум-эмодзи. Проверяем только форму
    // номера: по нему собирается адрес картинки на нашем же сервере.
    const rawBadge = parsed.badge;
    const badge =
      rawBadge &&
      typeof rawBadge === 'object' &&
      typeof rawBadge.id === 'string' &&
      /^[A-Za-z0-9_-]{1,128}$/.test(rawBadge.id)
        ? rawBadge
        : null;

    return { face, color, photoUrl, badge };
  } catch {
    return defaultAvatar();
  }
}

export function setAvatar(avatar: Avatar | ((current: Avatar) => Avatar)): void {
  try {
    const next = typeof avatar === 'function' ? avatar(getAvatar()) : avatar;
    window.localStorage.setItem(PROFILE_KEY, JSON.stringify(next));
  } catch {
    /* приватный режим — обойдёмся без запоминания */
  }
}

// ─────────────────────────── счётчики и история ───────────────────────────

/**
 * Личная статистика.
 *
 * Считается в браузере и никуда не уходит: аккаунтов нет, а привязывать
 * счётчики к устройству на сервере значило бы завести профиль тайком.
 * Поэтому цифры честно «этого браузера», о чём сказано и на экране.
 */
export interface Stats {
  firstSeenAt: number;
  roomsCreated: number;
  reactionsSent: number;
  spoilersSent: number;
  strokesDrawn: number;
  /** Секунды, проведённые в комнате с идущим видео. */
  watchSeconds: number;
  invitesCopied: number;
  /** Комнаты, где было четверо и больше. */
  crowdedRooms: number;
  /** Сеансы, начатые после полуночи. */
  lateNights: number;
}

export interface HistoryEntry {
  code: string;
  name: string;
  /** Что смотрели, если успели выбрать. */
  title: string | null;
  at: number;
  /** Имена соседей по комнате. */
  withWhom: string[];
}

const STATS_KEY = 'kinop:stats';
const HISTORY_KEY = 'kinop:history';
const HISTORY_LIMIT = 20;

const EMPTY_STATS: Stats = {
  firstSeenAt: 0,
  roomsCreated: 0,
  reactionsSent: 0,
  spoilersSent: 0,
  strokesDrawn: 0,
  watchSeconds: 0,
  invitesCopied: 0,
  crowdedRooms: 0,
  lateNights: 0,
};

export function getStats(): Stats {
  try {
    const raw = window.localStorage.getItem(STATS_KEY);
    const parsed = raw ? (JSON.parse(raw) as Partial<Stats>) : {};
    const stats = { ...EMPTY_STATS, ...parsed };
    // Первый заход мог случиться до того, как появились счётчики.
    if (!stats.firstSeenAt) stats.firstSeenAt = Date.now();
    return stats;
  } catch {
    return { ...EMPTY_STATS, firstSeenAt: Date.now() };
  }
}

/** Прибавляет к счётчикам. Значения только растут — обнулять их незачем. */
export function bumpStats(patch: Partial<Stats>): void {
  try {
    const current = getStats();
    const next: Stats = { ...current };
    for (const [key, value] of Object.entries(patch) as [keyof Stats, number][]) {
      next[key] = key === 'firstSeenAt' ? current[key] || value : current[key] + value;
    }
    window.localStorage.setItem(STATS_KEY, JSON.stringify(next));
  } catch {
    /* приватный режим — обойдёмся без запоминания */
  }
}

export function getHistory(): HistoryEntry[] {
  try {
    const raw = window.localStorage.getItem(HISTORY_KEY);
    return raw ? (JSON.parse(raw) as HistoryEntry[]) : [];
  } catch {
    return [];
  }
}

/**
 * Запоминает посещённую комнату.
 *
 * Одна комната — одна запись: за вечер в неё заходят и выходят по десять
 * раз, и десять одинаковых строк в истории никому не нужны.
 */
export function rememberRoom(entry: HistoryEntry): void {
  try {
    const history = getHistory().filter((item) => item.code !== entry.code);
    history.unshift(entry);
    window.localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, HISTORY_LIMIT)));
  } catch {
    /* приватный режим — обойдёмся без запоминания */
  }
}

// ─────────────────────────── библиотека стикеров ───────────────────────────

/**
 * Что у человека есть из стикеров.
 *
 * Наборы на сервере общие — там лежат сами файлы и состав, — а вот какие из
 * них показывать, недавние и избранные у каждого свои. Хранятся здесь же,
 * рядом с аватаркой и счётчиками: аккаунтов у нас нет, и заводить их ради
 * списка наборов не нужно.
 */
const STICKERS_KEY = 'kinop:stickers';

export interface StickerLibrary {
  /** Имена наборов, которые человек добавил себе. */
  installed: string[];
  /** Недавно отправленные, свежие в начале. */
  recent: string[];
  /** Избранные, в порядке добавления. */
  favourites: string[];
}

/** Недавних держим немного: это быстрый доступ, а не архив. */
const RECENT_LIMIT = 24;
/** Избранных больше, но тоже не бесконечно. */
const FAVOURITE_LIMIT = 60;

const EMPTY_LIBRARY: StickerLibrary = { installed: [], recent: [], favourites: [] };

function readStrings(value: unknown, limit: number): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is string => typeof item === 'string' && item.length > 0 && item.length <= 128)
    .slice(0, limit);
}

export function getStickerLibrary(): StickerLibrary {
  try {
    const raw = window.localStorage.getItem(STICKERS_KEY);
    if (!raw) return EMPTY_LIBRARY;
    const parsed = JSON.parse(raw) as Partial<StickerLibrary>;
    return {
      installed: readStrings(parsed.installed, 100),
      recent: readStrings(parsed.recent, RECENT_LIMIT),
      favourites: readStrings(parsed.favourites, FAVOURITE_LIMIT),
    };
  } catch {
    return EMPTY_LIBRARY;
  }
}

function saveLibrary(library: StickerLibrary): StickerLibrary {
  try {
    window.localStorage.setItem(STICKERS_KEY, JSON.stringify(library));
  } catch {
    /* приватный режим — обойдёмся без запоминания */
  }
  return library;
}

export function installPack(name: string): StickerLibrary {
  const library = getStickerLibrary();
  if (library.installed.includes(name)) return library;
  return saveLibrary({ ...library, installed: [...library.installed, name] });
}

export function uninstallPack(name: string): StickerLibrary {
  const library = getStickerLibrary();
  return saveLibrary({
    ...library,
    installed: library.installed.filter((item) => item !== name),
  });
}

/** Отправленный стикер уходит в начало недавних, без повторов. */
export function rememberRecentSticker(id: string): StickerLibrary {
  const library = getStickerLibrary();
  const recent = [id, ...library.recent.filter((item) => item !== id)].slice(0, RECENT_LIMIT);
  return saveLibrary({ ...library, recent });
}

export function toggleFavouriteSticker(id: string): StickerLibrary {
  const library = getStickerLibrary();
  const has = library.favourites.includes(id);
  const favourites = has
    ? library.favourites.filter((item) => item !== id)
    : [...library.favourites, id].slice(-FAVOURITE_LIMIT);
  return saveLibrary({ ...library, favourites });
}

/**
 * Недавние эмодзи.
 *
 * Их почти две тысячи, и без этой полки каждый раз пришлось бы искать
 * заново те же десять штук, которыми пользуешься.
 */
const RECENT_EMOJI_KEY = 'kinop:emoji-recent';
const RECENT_EMOJI_LIMIT = 32;

export function getRecentEmoji(): string[] {
  try {
    const raw = window.localStorage.getItem(RECENT_EMOJI_KEY);
    if (!raw) return [];
    return readStrings(JSON.parse(raw), RECENT_EMOJI_LIMIT);
  } catch {
    return [];
  }
}

export function rememberRecentEmoji(emoji: string): string[] {
  const next = [emoji, ...getRecentEmoji().filter((item) => item !== emoji)].slice(
    0,
    RECENT_EMOJI_LIMIT,
  );
  try {
    window.localStorage.setItem(RECENT_EMOJI_KEY, JSON.stringify(next));
  } catch {
    /* приватный режим — обойдёмся без запоминания */
  }
  return next;
}

export interface RoomDefaults { anyonePause: boolean; reactions: boolean; drawing: boolean; hideEnding: boolean; trailSeconds: number; }
export function getRoomDefaults(): RoomDefaults {
  const base = {anyonePause: true, reactions: true, drawing: true, hideEnding: false, trailSeconds: 8};
  try { return {...base, ...JSON.parse(readRaw('kinop:room-defaults') ?? '{}')}; } catch { return base; }
}
export function saveRoomDefaults(value: RoomDefaults): void { writeRaw('kinop:room-defaults', JSON.stringify(value)); }
